import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Box, Edit, AlertCircle, MapPin, ArrowUpCircle, ArrowDownCircle, History, DollarSign } from 'lucide-react';
import { RawMaterialFormDialog } from '@/components/Inventory/RawMaterialFormDialog';
import { RawMaterialMovementDialog } from '@/components/Inventory/RawMaterialMovementDialog';
import type { RawMaterial } from '@/types/legacy';
import { differenceInDays, format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  useRawMaterialMovements,
  useRawMaterialCategories,
  getMovementTypeLabel,
  getMovementTypeColor,
  getRawMaterialReasonLabel,
} from '@/hooks/useRawMaterialMovements';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface RawMaterialsPageProps {
  embedded?: boolean;
}

export default function RawMaterialsPage({ embedded = false }: RawMaterialsPageProps) {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const canSeeCosts = ['admin', 'supervisor', 'plotter'].includes(userRole || '');
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<RawMaterial | null>(null);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [movementType, setMovementType] = useState<'in' | 'out' | 'adjustment'>('in');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedMaterialForMovement, setSelectedMaterialForMovement] = useState<RawMaterial | null>(null);

  const { data: customCategories } = useRawMaterialCategories();
  const { data: movements } = useRawMaterialMovements(selectedMaterialForMovement?.id);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const { data, error } = await api
        .from('raw_materials')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMaterials(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las materias primas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  useDataRefresh(fetchMaterials, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredMaterials = materials.filter(material =>
    material.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    material.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedMaterial(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const expiringMaterials = materials.filter(m => {
    if (!m.expiry_date) return false;
    const daysUntilExpiry = differenceInDays(new Date(m.expiry_date), new Date());
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  });

  const categoriesCount = new Set(materials.map(m => m.category)).size;

  const totalValue = materials.reduce(
    (sum, m) => sum + (m.quantity || 0) * (m.cost_per_unit || 0),
    0
  );

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      resin: 'Resina',
      fiber: 'Fibra',
      fabric: 'Tela',
      core: 'Núcleo',
      consumable: 'Consumible',
      catalyst: 'Catalizador',
      other: 'Otro',
    };
    // Check if it's a system label first
    if (labels[category]) return labels[category];
    // Check custom categories
    const custom = customCategories?.find(c => c.name === category);
    if (custom) return custom.description || custom.name;
    // Return as-is if not found (it's a custom category name)
    return category;
  };

  const handleOpenMovement = (material: RawMaterial, type: 'in' | 'out') => {
    setSelectedMaterialForMovement(material);
    setMovementType(type);
    setMovementDialogOpen(true);
  };

  const handleOpenHistory = (material: RawMaterial) => {
    setSelectedMaterialForMovement(material);
    setHistoryDialogOpen(true);
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const daysUntilExpiry = differenceInDays(new Date(expiryDate), new Date());
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const content = (
    <>
      <div className={embedded ? "space-y-6" : "space-y-6 p-6"}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Materias Primas
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de materias primas
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Materia Prima
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar materias primas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {materials.length}
                </p>
              </div>
              <Box className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          {canSeeCosts && (
            <Card className="glass-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valor Estimado</p>
                  <p className="text-3xl font-bold text-emerald-600">
                    €{totalValue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500/50" />
              </div>
            </Card>
          )}

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Próximos a Vencer</p>
                <p className="text-3xl font-bold text-warning">
                  {expiringMaterials.length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-warning/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Categorías</p>
                <p className="text-3xl font-bold text-primary">
                  {categoriesCount}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Materials Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredMaterials.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Box className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron materias primas' : 'No hay materias primas registradas'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMaterials.map((material) => {
              const expiringSoon = isExpiringSoon(material.expiry_date);
              const expiringLabel = material.is_freezer_material ? 'Caduca pronto' : 'Vence pronto';
              const hasFreezerDetails = material.is_freezer_material && (
                material.roll_count != null ||
                material.batch_number ||
                material.roll_number ||
                material.production_date ||
                material.expiry_date
              );

              return (
                <Card key={material.id} className="glass-card p-6 hover:shadow-magma transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Box className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{material.name}</h3>
                        {material.code && (
                          <p className="text-sm text-muted-foreground">{material.code}</p>
                        )}
                      </div>
                    </div>
                    {!material.is_active && (
                      <Badge variant="secondary" className="text-xs">
                        Inactivo
                      </Badge>
                    )}
                  </div>

                  <div className="mb-3 flex flex-wrap gap-2">
                    <Badge variant="outline">{getCategoryLabel(material.category)}</Badge>
                    {material.is_freezer_material && (
                      <Badge variant="secondary">Congelador</Badge>
                    )}
                    {!material.is_freezer_material && material.batch_number && (
                      <Badge variant="secondary">Lote: {material.batch_number}</Badge>
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Cantidad:</span>
                      <span className="text-sm font-medium">{material.quantity} {material.unit}</span>
                    </div>

                    {material.location && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Ubicación:
                        </span>
                        <span className="text-sm font-medium">{material.location}</span>
                      </div>
                    )}

                    {!material.is_freezer_material && material.expiry_date && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Vencimiento:</span>
                        <span className={`text-sm ${expiringSoon ? 'text-warning font-medium' : ''}`}>
                          {new Date(material.expiry_date).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    )}

                    {expiringSoon && (
                      <div className="flex items-center gap-2 text-warning mt-2">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">{expiringLabel}</span>
                      </div>
                    )}
                  </div>

                  {material.is_freezer_material && (
                    <div className="mb-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary/70 mb-2">
                        Zona de congeladores
                      </p>
                      {hasFreezerDetails ? (
                        <div className="space-y-1 text-sm">
                          {material.roll_count != null && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Bobinas/Rollos:</span>
                              <span className="font-medium">{material.roll_count}</span>
                            </div>
                          )}
                          {material.batch_number && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Lote:</span>
                              <span className="font-medium">{material.batch_number}</span>
                            </div>
                          )}
                          {material.roll_number && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Rollo:</span>
                              <span className="font-medium">{material.roll_number}</span>
                            </div>
                          )}
                          {material.production_date && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Produccion:</span>
                              <span className="font-medium">
                                {new Date(material.production_date).toLocaleDateString('es-ES')}
                              </span>
                            </div>
                          )}
                          {material.expiry_date && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Caducidad:</span>
                              <span className={`font-medium ${expiringSoon ? 'text-warning' : ''}`}>
                                {new Date(material.expiry_date).toLocaleDateString('es-ES')}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Sin datos de congelador</p>
                      )}
                    </div>
                  )}

                  {/* Movement buttons - always visible */}
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMovement(material, 'in')}
                        className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                      >
                        <ArrowUpCircle className="w-4 h-4 mr-1" />
                        Entrada
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMovement(material, 'out')}
                        className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <ArrowDownCircle className="w-4 h-4 mr-1" />
                        Salida
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenHistory(material)}
                        className="flex-1 text-muted-foreground"
                      >
                        <History className="w-4 h-4 mr-1" />
                        Historial
                      </Button>
                      {canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(material)}
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <RawMaterialFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          rawMaterial={selectedMaterial}
          onSuccess={fetchMaterials}
        />

        {/* Movement Dialog */}
        {selectedMaterialForMovement && (
          <RawMaterialMovementDialog
            open={movementDialogOpen}
            onOpenChange={setMovementDialogOpen}
            materialId={selectedMaterialForMovement.id}
            materialName={selectedMaterialForMovement.name}
            currentStock={selectedMaterialForMovement.quantity}
            currentSquareMeters={selectedMaterialForMovement.square_meters}
            unit={selectedMaterialForMovement.unit}
            movementType={movementType}
            onSuccess={fetchMaterials}
          />
        )}

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial de Movimientos
              </DialogTitle>
              <DialogDescription>
                {selectedMaterialForMovement ? selectedMaterialForMovement.name : 'Historial de movimientos de inventario'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {movements && movements.length > 0 ? (
                movements.map((mov) => (
                  <div
                    key={mov.id}
                    className="flex items-start justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-start gap-3">
                      <Badge className={getMovementTypeColor(mov.movement_type)}>
                        {getMovementTypeLabel(mov.movement_type)}
                      </Badge>
                      <div>
                        <p className="font-medium">
                          {mov.movement_type === 'out' ? '-' : mov.movement_type === 'in' ? '+' : ''}
                          {Math.abs(mov.quantity)} {selectedMaterialForMovement?.unit}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {getRawMaterialReasonLabel(mov.reason)}
                        </p>
                        {mov.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {mov.notes}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Stock: {mov.previous_stock} → {mov.new_stock}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <p>{format(new Date(mov.created_at), "dd/MM/yyyy HH:mm", { locale: es })}</p>
                      {mov.performed_by_profile && (
                        <p>{mov.performed_by_profile.full_name}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No hay movimientos registrados
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );

  return embedded ? content : (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}
